import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { ADD_USER_STEP } from './tourRegistry';
import {
	subscribeUserFormComplexity,
	getUserFormComplexity,
} from './addUserTourState';

const TOUR_KEY = 'canapps_add_user';

// Drives the "Add a user" walkthrough once we're on the Users page. Called from
// Users.jsx with the form's open flag; reads the view complexity from a store
// (the toggle is local React state with no DOM signal). Gates:
//   USERS_ITEM -> NEW_USER   arrived on the Users page
//   NEW_USER   -> VIEW_FULL  the user form opened
//   VIEW_FULL  -> FIRST_NAME the "Full" view was selected
//   SAVE       -> FINISH     the form closed (saved) after Save
// The field steps in between are Next-driven.
export const useAddUserTourSync = (formOpen: boolean) => {
	const { isOpen, currentStep, setCurrentStep } = useTour();
	const complexity = useSyncExternalStore(
		subscribeUserFormComplexity,
		getUserFormComplexity,
	);
	const prevFormOpenRef = useRef(formOpen);

	useEffect(() => {
		const justClosed = prevFormOpenRef.current && !formOpen;
		prevFormOpenRef.current = formOpen;

		if (!isOpen || getActiveTourKey() !== TOUR_KEY) return;

		if (currentStep === ADD_USER_STEP.USERS_ITEM) {
			// This hook only mounts on the Users page, so arriving here means the
			// sidebar navigation is done.
			setCurrentStep(ADD_USER_STEP.NEW_USER);
		} else if (currentStep === ADD_USER_STEP.NEW_USER && formOpen) {
			setCurrentStep(ADD_USER_STEP.VIEW_FULL);
		} else if (
			currentStep === ADD_USER_STEP.VIEW_FULL &&
			complexity === 'full'
		) {
			setCurrentStep(ADD_USER_STEP.FIRST_NAME);
		} else if (currentStep === ADD_USER_STEP.SAVE && justClosed) {
			setCurrentStep(ADD_USER_STEP.FINISH);
		}
	}, [isOpen, currentStep, formOpen, complexity, setCurrentStep]);
};
